// Swiper Gallery Component
// Handles initialization and configuration of Swiper galleries

import Swiper from 'swiper';
import { Navigation, Scrollbar, Autoplay, Keyboard, A11y, FreeMode, EffectFade, Zoom } from 'swiper/modules';

export class SwiperGallery {
  private galleries: NodeListOf<HTMLElement>;
  private swiperInstances: Swiper[] = [];

  constructor() {
    this.galleries = document.querySelectorAll('[data-swiper-gallery]');
    this.init();
  }

  private init(): void {
    if (this.galleries.length === 0) return;

    this.galleries.forEach((gallery) => {
      const galleryType = gallery.getAttribute('data-gallery-type');
      let mainSwiper: Swiper | null = null;

      // Initialize Main Swiper (if not topToBottom)
      if (galleryType !== 'topToBottom') {
        // Select the direct .swiper child to avoid finding the lightbox one
        // Since :scope isn't always reliable in all contexts, we can look for .swiper that is NOT .swiper-lightbox__container
        const swiperContainer = Array.from(gallery.querySelectorAll('.swiper')).find(
          el => !el.classList.contains('swiper-lightbox__container')
        ) as HTMLElement;
        
        if (swiperContainer) {
          const config = this.getConfig(gallery);
          if (config) {
            mainSwiper = new Swiper(swiperContainer, config);
            this.swiperInstances.push(mainSwiper);
            this.preventSelectionOnNavButtons(gallery);
          }
        }
      }

      // Initialize Lightbox (for all types)
      this.initLightbox(gallery, mainSwiper);
    });
  }

  private initLightbox(gallery: HTMLElement, mainSwiper: Swiper | null): void {
    const lightbox = gallery.querySelector('.swiper-lightbox') as HTMLElement;
    if (!lightbox) return;

    const lightboxContainer = lightbox.querySelector('.swiper-lightbox__container') as HTMLElement;
    const closeBtn = lightbox.querySelector('.swiper-lightbox__close');
    
    // Lightbox Swiper Config
    const lightboxConfig: any = {
      modules: [Navigation, Keyboard, A11y, Zoom],
      slidesPerView: 1,
      spaceBetween: 30,
      loop: true,
      observer: true,
      observeParents: true,
      zoom: true,
      navigation: {
        nextEl: lightbox.querySelector('.swiper-button-next') as HTMLElement,
        prevEl: lightbox.querySelector('.swiper-button-prev') as HTMLElement,
      },
      keyboard: {
        enabled: true,
      },
      a11y: {
        enabled: true,
      },
      initialSlide: mainSwiper ? mainSwiper.realIndex : 0,
    };

    const lightboxSwiper = new Swiper(lightboxContainer, lightboxConfig);
    this.swiperInstances.push(lightboxSwiper);

    // Prevent selection on lightbox nav buttons too
    this.preventSelectionOnNavButtons(lightbox);

    // Sync: Lightbox -> Main (only if mainSwiper exists)
    lightboxSwiper.on('slideChange', () => {
      if (mainSwiper && !mainSwiper.destroyed) {
        if (mainSwiper.params.loop) {
          mainSwiper.slideToLoop(lightboxSwiper.realIndex);
        } else {
          mainSwiper.slideTo(lightboxSwiper.realIndex);
        }
      }
    });

    // Open Lightbox Triggers
    const galleryType = gallery.getAttribute('data-gallery-type');

    if (galleryType === 'topToBottom') {
      // For topToBottom, clicks are on static images
      const images = gallery.querySelectorAll('.swiper-gallery__image');
      images.forEach((img) => {
        img.addEventListener('click', () => {
          const index = parseInt(img.getAttribute('data-index') || '0', 10);
          lightboxSwiper.slideToLoop(index, 0);
          lightbox.classList.add('is-active');
        });
      });
    } else {
      // For others, clicks are on swiper slides
      const mainSlides = gallery.querySelectorAll('.swiper:not(.swiper-lightbox__container) .swiper-slide');
      mainSlides.forEach((slide, index) => {
        slide.addEventListener('click', () => {
          // Determine the real index of the clicked slide
          const loopIndex = slide.getAttribute('data-swiper-slide-index');
          const realIndex = loopIndex !== null ? parseInt(loopIndex, 10) : index;
          
          lightboxSwiper.slideToLoop(realIndex, 0);
          
          lightbox.classList.add('is-active');
        });
      });
    }

    // Close Lightbox
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        lightbox.classList.remove('is-active');
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox.classList.contains('is-active')) {
        lightbox.classList.remove('is-active');
      }
    });
  }

  private preventSelectionOnNavButtons(gallery: HTMLElement): void {
    const prevBtn = gallery.querySelector('.swiper-button-prev');
    const nextBtn = gallery.querySelector('.swiper-button-next');

    [prevBtn, nextBtn].forEach(btn => {
      if (btn) {
        // Prevent default on mousedown to stop text selection start
        btn.addEventListener('mousedown', (e) => {
          e.preventDefault();
        });
        
        // Stop propagation on click as requested
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      }
    });
  }

  private getConfig(gallery: HTMLElement): any | null {
    const galleryType = gallery.getAttribute('data-gallery-type') || 'standardMitPfeilenLinksRechts';
    const autoplayEnabled = gallery.getAttribute('data-autoplay') === '1';
    const sliderSpeedMsRaw = gallery.getAttribute('data-slider-speed-ms');
    const sliderSpeedMs = sliderSpeedMsRaw ? Number(sliderSpeedMsRaw) : null;

    if (galleryType === 'topToBottom') return null;

    if (galleryType === 'sideSliderSlidedNachRechts') {
      const config: any = {
        modules: [Scrollbar, FreeMode, Keyboard, A11y, Zoom],
        slidesPerView: 'auto',
        spaceBetween: 16,
        freeMode: true,
        loop: false,
        grabCursor: true,
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
        },
        zoom: true,
      };

      const swiperContainer = Array.from(gallery.querySelectorAll('.swiper')).find(
        el => !el.classList.contains('swiper-lightbox__container')
      ) as HTMLElement;
      
      if (swiperContainer) {
        const swiper = new Swiper(swiperContainer, config);
        this.swiperInstances.push(swiper);
        this.preventSelectionOnNavButtons(gallery);
        
        // Add custom scroll handlers for fixed amount scrolling
        const prevBtn = gallery.querySelector('.swiper-button-prev') as HTMLElement;
        const nextBtn = gallery.querySelector('.swiper-button-next') as HTMLElement;
        
        const scrollAmount = 300;
        
        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const currentTranslate = swiper.getTranslate();
            const newTranslate = Math.min(0, currentTranslate + scrollAmount);
            swiper.translateTo(newTranslate, 300);
          });
        }
        
        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const currentTranslate = swiper.getTranslate();
            const maxTranslate = -swiper.snapGrid[swiper.snapGrid.length - 1];
            const newTranslate = Math.max(maxTranslate, currentTranslate - scrollAmount);
            swiper.translateTo(newTranslate, 300);
          });
        }
      }
      
      return null;
    }

    const config: any = {
      modules: [Navigation, Autoplay, Keyboard, A11y, Zoom],
      slidesPerView: 1,
      spaceBetween: 30,
      loop: true,
      zoom: true,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
      },
    };

    // Use fade effect for the standard gallery variant
    if (galleryType === 'standardMitPfeilenLinksRechts') {
      config.modules.push(EffectFade);
      config.effect = 'fade';
      config.fadeEffect = { crossFade: true };
      // Increase transition duration for a smoother fade (in ms)
      config.speed = 900;
    }

    if (galleryType === 'homepageGallery' || galleryType === 'standardMitPfeilenLinksRechts') {
      if (autoplayEnabled) {
        config.autoplay = {
          delay: sliderSpeedMs && sliderSpeedMs > 0 ? sliderSpeedMs : 5000,
          disableOnInteraction: false,
        };
      } else {
        config.autoplay = false;
      }
    } else {
      config.autoplay = false;
    }

    return config;
  }
}
